import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@massilia/ui';
import { Badge } from '@massilia/ui';
import { CommandGroup, CommandInput, CommandItem, CommandList } from '@massilia/ui';
import { Label } from '@massilia/ui';
import { Skeleton } from '@massilia/ui';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFieldContext } from '../hooks/use-app-form.js';
import { FieldError } from './field-error';
const STABLE_ARRAY = [];
export default function MultiSelectAutocompleteField({ disabled, emptyMessage = 'Aucun résultat trouvé', isLoading = false, label, options, placeholder = 'Rechercher...', }) {
    const field = useFieldContext();
    const errors = field.state.meta.errors;
    const selectedValues = field.state.value ?? STABLE_ARRAY;
    const inputRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    // Filter options based on search text (client-side filtering)
    const filteredOptions = useMemo(() => {
        if (!inputValue)
            return options;
        const search = inputValue.toLowerCase();
        return options.filter((option) => option.label.toLowerCase().includes(search));
    }, [options, inputValue]);
    const handleSelect = useCallback((option) => {
        const isAlreadySelected = selectedValues.includes(option.value);
        const newValues = isAlreadySelected
            ? selectedValues.filter((v) => v !== option.value)
            : [...selectedValues, option.value];
        field.handleChange(newValues);
        setInputValue('');
    }, [selectedValues, field]);
    const handleRemove = useCallback((value) => {
        field.handleChange(selectedValues.filter((v) => v !== value));
    }, [selectedValues, field]);
    const handleKeyDown = useCallback((event) => {
        if (!isOpen) {
            setIsOpen(true);
        }
        if (event.key === 'Escape') {
            inputRef.current?.blur();
        }
        // Backspace removes last selected item when input is empty
        if (event.key === 'Backspace' && inputValue === '' && selectedValues.length > 0) {
            field.handleChange(selectedValues.slice(0, -1));
        }
    }, [isOpen, inputValue, selectedValues, field]);
    const handleBlur = useCallback(() => {
        setIsOpen(false);
        setInputValue('');
    }, []);
    // Compute dropdown position via portal
    const [dropdownStyle, setDropdownStyle] = useState();
    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownStyle({
                left: rect.left + window.scrollX,
                position: 'absolute',
                top: rect.bottom + window.scrollY,
                width: rect.width,
                zIndex: 60,
            });
        }
    }, [isOpen]);
    const dropdown = (_jsx("div", { className: cn('animate-in fade-in-0 zoom-in-95 border-input rounded-lg border bg-white shadow-md outline-none', isOpen && !disabled ? 'block' : 'hidden'), style: dropdownStyle, children: _jsxs(CommandList, { className: "max-h-48 rounded-lg", children: [isLoading ? (_jsx(CommandPrimitive.Loading, { children: _jsx("div", { className: "p-1", children: _jsx(Skeleton, { className: "h-8 w-full" }) }) })) : null, filteredOptions.length > 0 && !isLoading ? (_jsx(CommandGroup, { children: filteredOptions.map((option) => {
                        const isSelected = selectedValues.includes(option.value);
                        return (_jsxs(CommandItem, { className: "flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 hover:bg-gray-100", onMouseDown: (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }, onSelect: () => {
                                handleSelect(option);
                            }, value: option.label, children: [_jsx("div", { className: "flex h-4 w-4 items-center justify-center", children: isSelected ? _jsx(Check, { className: "h-4 w-4" }) : null }), _jsx("span", { className: "flex-1 text-left", children: option.label })] }, option.value));
                    }) })) : null, !isLoading && filteredOptions.length === 0 ? (_jsx(CommandPrimitive.Empty, { className: "rounded-sm px-2 py-3 text-center text-sm text-gray-500 select-none", children: emptyMessage })) : null] }) }));
    return (_jsxs("div", { className: "flex flex-1 flex-col gap-2", children: [label && _jsx(Label, { className: "text-sm font-medium md:text-[16px]", children: label }), _jsxs(CommandPrimitive, { className: "w-full", onKeyDown: handleKeyDown, shouldFilter: false, children: [_jsx(CommandInput, { className: "border-input bg-background text-foreground focus-visible:border-ring focus-visible:ring-ring/20 flex h-9 w-full cursor-text rounded-lg border px-3 py-2 text-sm shadow-sm shadow-black/5 transition-shadow placeholder:text-gray-500 focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50", disabled: disabled, onBlur: handleBlur, onFocus: () => {
                            setIsOpen(true);
                        }, onValueChange: setInputValue, placeholder: placeholder, ref: inputRef, value: inputValue }), isOpen && createPortal(dropdown, document.body)] }), selectedValues.length > 0 && (_jsx("div", { className: "flex flex-wrap gap-1", children: selectedValues.map((value) => {
                    const option = options.find((o) => o.value === value);
                    return (_jsxs(Badge, { className: "flex cursor-default items-center gap-1 border-blue-200 bg-blue-50 pr-1 text-blue-700 hover:bg-blue-100", variant: "outline", children: [option?.label ?? value, _jsx("button", { className: "rounded-full p-0.5 hover:bg-blue-200", onClick: () => {
                                    handleRemove(value);
                                }, type: "button", children: _jsx(X, { className: "h-3 w-3" }) })] }, value));
                }) })), _jsx(FieldError, { errors: errors })] }));
}
